/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

export type UseGetServicingInstallments = {
  corpAdvFeeBal?: number;
  assessmentFeeBal?: number;
  lateFeeBal?: number;
  ti?: number;
  borrowerPiti?: number;
  dueDate?: number;
  prinSubsidy?: number;
  pi?: number;
  intSubsidy?: number;
  errors?: string;
  changeType?: string; //Only available when the dateString is 'next-change'
};

const useGetServicingInstallments = (loanNumber, date) => {
  const { token } = useAuthContext();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [loanNumber, date, token], [loanNumber, date, token]);

  const reloadServInstData = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!loanNumber || !token || !date) return { data: [] };
      try {
        const request = {
          url: `live/servicing/loan/${loanNumber}/installment/${date}`,
          method: 'GET',
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        // console.log(`useGetServicingInstallments.response: `, response);
        return { data: response };
      } catch (error) {
        console.error(`useGetServicingInstallments.caught: ${error.message}`);
        return { data: [] };
      }
    },
  });

  const servInstData = useMemo((): UseGetServicingInstallments => {
    if (isLoading) return {};
    if (!data || data === null) return {};
    return data.data as UseGetServicingInstallments;
  }, [isLoading, data]);

  return {
    servInstData,
    reloadServInstData,
  };
};

export default useGetServicingInstallments;
